import { useRef, useLayoutEffect, useState, createPortal } from '@wordpress/element';

/**
 * Render a React component relative to a DOM element
 *
 * @param {Object}                                            props            Component props The component props
 * @param {string}                                            props.target     The target DOM selector
 * @param {string}                                            props.className  The class name to apply to the root element
 * @param {JSX.Element}                                       props.children   The component to render
 * @param {'beforebegin'|'afterbegin'|'beforeend'|'afterend'} props.position   The position to insert the new element
 * @param {boolean}                                           props.hideTarget Whether to hide the target element
 *
 * @return {JSX.Element | null}                                                 The rendered component or null
 */
function RenderContent({ target, className, children, position = 'afterend', hideTarget }) {
  const targetNode = useRef(null);
  const observerRef = useRef(null);
  const [newElement, setNewElement] = useState(null);

  useLayoutEffect(() => {
    targetNode.current = document.querySelector(target);

    if (!newElement) {
      const element = document.createElement('div');
      element.className = className;
      setNewElement(element);
    }

    /**
     * Insert the new element into the DOM
     */
    const insertElement = () => {
      const currentTarget = document.querySelector(target);

      if (currentTarget?.parentNode && newElement && !newElement.isConnected) {
        currentTarget.insertAdjacentElement(position, newElement);

        if (hideTarget) {
          currentTarget.style.opacity = 0;
          currentTarget.setAttribute('aria-hidden', 'true');
          currentTarget.style.pointerEvents = 'none';
        }
      }
    };

    observerRef.current = new MutationObserver(() => {
      insertElement();
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observerRef.current?.disconnect();

      if (newElement?.parentNode) {
        newElement.parentNode.removeChild(newElement);
      }

      const currentTarget = document.querySelector(target);
      if (currentTarget && hideTarget) {
        currentTarget.style.opacity = 1;
        currentTarget.setAttribute('aria-hidden', null);
        currentTarget.style.pointerEvents = 'auto';
      }
    };
  }, [target, newElement, hideTarget, className, position]);

  return newElement ? createPortal(children, newElement) : null;
}

export default RenderContent;
